import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import config from '../config.js'

export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref(localStorage.getItem('admin_token') || null)
  const client = ref(null)
  const isAuthenticated = computed(() => !!token.value)
  const isLoading = ref(false)
  
  // Router instance (will be set when store is used)
  let router = null

  // Constants for safety
  const MAX_AUTH_ATTEMPTS = 1 // No retries - fail fast
  const TOKEN_EXPIRY_WARNING_HOURS = 1 // Warn 1 hour before expiry



  // Getters
  const hasValidToken = computed(() => {
    if (!token.value) return false
    
    try {
      // Decode JWT to check expiration (client-side validation)
      const payload = JSON.parse(atob(token.value.split('.')[1]))
      const now = Math.floor(Date.now() / 1000)
      return payload.exp > now
    } catch (error) {
      console.error('Error parsing token:', error)
      return false
    }
  })

  const isTokenExpiringSoon = computed(() => {
    if (!token.value) return false
    
    try {
      const payload = JSON.parse(atob(token.value.split('.')[1]))
      const now = Math.floor(Date.now() / 1000)
      const warningTime = now + (TOKEN_EXPIRY_WARNING_HOURS * 3600)
      return payload.exp <= warningTime
    } catch (error) {
      return false
    }
  })

  // Actions
  const setRouter = (routerInstance) => {
    router = routerInstance
  }

  const setToken = (newToken) => {
    token.value = newToken
    if (newToken) {
      localStorage.setItem('admin_token', newToken)
    } else {
      localStorage.removeItem('admin_token')
    }
  }

  const setClient = (clientData) => {
    client.value = clientData
  }

  const login = async (email, password) => {
    if (isLoading.value) return { success: false, error: 'Login already in progress' }
    
    isLoading.value = true
    
    try {
      const response = await fetch(`${config.apiBaseUrl}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Login failed: ${response.status}`)
      }

      const data = await response.json()
      
      // Set token and client data
      setToken(data.token)
      setClient(data.client)
      
      return { success: true, data }
      
    } catch (error) {
      console.error('Login error:', error)
      return { success: false, error: error.message }
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    try {
      // Attempt to call logout endpoint (but don't wait for it)
      if (token.value) {
        fetch(`${config.apiBaseUrl}/admin/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token.value}`
          }
        }).catch(() => {
          // Ignore logout endpoint errors - we're logging out anyway
        })
      }
    } catch (error) {
      // Ignore any errors during logout
    } finally {
      // Always clear local state regardless of API response
      setToken(null)
      setClient(null)
      
      // Redirect to login
      if (router) {
        router.push('/admin/login')
      }
    }
  }

  const validateToken = async () => {
    // Safety check: prevent multiple simultaneous validations
    if (isLoading.value) return false
    
    if (!token.value || !hasValidToken.value) {
      await logout()
      return false
    }

    isLoading.value = true
    
    try {
      const response = await fetch(`${config.apiBaseUrl}/admin/client`, {
        headers: {
          'Authorization': `Bearer ${token.value}`
        }
      })

      if (response.status === 401) {
        // Token is invalid - logout immediately
        await logout()
        return false
      }

      if (!response.ok) {
        // Any other error - logout for safety
        await logout()
        return false
      }

      const data = await response.json()
      setClient(data.client)
      return true
      
    } catch (error) {
      console.error('Token validation error:', error)
      // Network error - logout for safety
      await logout()
      return false
    } finally {
      isLoading.value = false
    }
  }

  const checkAuthStatus = async () => {
    // Single validation per page load - no retries
    return await validateToken()
  }

  // Initialize
  const initialize = async (routerInstance) => {
    setRouter(routerInstance)
    
    // Check token validity on app start
    if (token.value) {
      await checkAuthStatus()
    }
  }

  return {
    // State
    token,
    client,
    isLoading,
    
    // Getters
    isAuthenticated,
    hasValidToken,
    isTokenExpiringSoon,
    
    // Actions
    login,
    logout,
    validateToken,
    checkAuthStatus,
    initialize,
    setToken,
    setClient
  }
})
